'use client';

import type { IconNameType } from '@wistia/ui';
import { Icon } from '@wistia/ui';
import { styled } from 'styled-components';
import type { ReactNode } from 'react';
import TextLink from './TextLink';

type AccordionProps = {
  /**
   * What the H4 says
   */
  headline: string;
  /**
   * Descriptive paragraph
   */
  description: ReactNode | string;
  /**
   * The name of the icon to be used
   */
  icon?: IconNameType;
  /**
   * What href should the button/link point to?
   */
  href?: string;
  /**
   * What the link should say.
   */
  ctaText?: string;
};

const AccordionContainer = styled.li`
  background-color: ${({ theme }) => theme.colors.white};
  display: flex;
  gap: ${({ theme }) => theme.spacing['1.5']};
  padding: ${({ theme }) => theme.spacing['1.5']};

  .headlineIcon {
    display: none;
  }

  .accordionIcon {
    color: ${({ theme }) => theme.colors.blue500};
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    padding: ${({ theme }) => `${theme.spacing['1.5']} ${theme.spacing['3']}`};

    .headlineIcon {
      display: initial;
    }

    &:hover {
      background-color: ${({ theme }) => theme.colors.blue100};
    }
  }
`;

const AccordionSummary = styled.summary`
  align-items: center;
  cursor: pointer;
  display: flex;
  justify-content: space-between;

  /* Hide the default Details discloure icon on Chrome and Firefox */
  list-style-type: none;

  /* Hide the default Details discloure icon on Safari  */
  &::-webkit-details-marker {
    display: none;
  }
`;

const AccordionDetails = styled.details`
  flex: 1;

  .accordionLink {
    color: ${({ theme }) => theme.colors.blue500};
    font-size: ${({ theme }) => theme.typography.fontSize.bodySm};

    ${({ theme }) => theme.mq.minWidth.large} {
      font-size: ${({ theme }) => theme.typography.fontSize.desktop.bodySm};
    }

    &:visited {
      color: ${({ theme }) => theme.colors.blue500};
    }
  }

  .collapseIcon {
    display: none;
  }

  &[open] {
    .expandIcon {
      display: none;
    }

    .collapseIcon {
      display: initial;
    }
  }
`;

const AccordionHeadline = styled.h4`
  display: inline-block;
  flex: 80%;
  margin: 0;
  padding-right: ${({ theme }) => theme.spacing['1']};
`;

const AccordionDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.bodySm};
  margin: 0;
  padding: ${({ theme }) => `${theme.spacing['0.75']} 0`};

  ${({ theme }) => theme.mq.minWidth.large} {
    font-size: ${({ theme }) => theme.typography.fontSize.desktop.bodySm};
  }

  & > *:last-child {
    margin-bottom: 0;
  }
`;

const Accordion = ({
  headline,
  description,
  icon,
  href,
  ctaText = 'Learn more',
}: AccordionProps) => {
  const descriptionIsString = typeof description === 'string';

  return (
    <AccordionContainer>
      {icon ? (
        <Icon
          className="accordionIcon headlineIcon"
          role="presentation"
          type={icon}
        />
      ) : null}
      <AccordionDetails>
        <AccordionSummary>
          <AccordionHeadline>{headline}</AccordionHeadline>
          <Icon
            className="accordionIcon expandIcon"
            role="presentation"
            type="plus"
          />

          <Icon
            className="accordionIcon collapseIcon"
            role="presentation"
            type="close"
          />
        </AccordionSummary>

        {descriptionIsString ? (
          <AccordionDescription>{description}</AccordionDescription>
        ) : (
          <AccordionDescription as="div">{description}</AccordionDescription>
        )}
        {href ? (
          <TextLink
            className="accordionLink"
            cta={{
              href,
              text: ctaText,
            }}
          />
        ) : undefined}
      </AccordionDetails>
    </AccordionContainer>
  );
};

export default Accordion;
